import type { CalendarEntry, CalendarList, CalendarRecord } from "@qendo/calendar";
import { type DateArg, differenceInDays } from "date-fns";
import { useCallback, useContext, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { AgendaList, CalendarContext } from "react-native-calendars";
import { EventListing } from "~/components/layout/calendar/EventListing";
import { JournalListing } from "~/components/layout/journal/JournalListing";
import { NoRecords, Title } from "~/components/layout/text";
import { Gap } from "~/constants/layout";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { formatDateString, formatShortDateForHumans, isSameDayOrEarlier } from "~/util/dates";
import { isJournal } from "~/util/qendo/calendar";

type CalendarGroupProps<T extends CalendarEntry> = {
    calendar: CalendarRecord<T>;
    asStatic?: boolean;
    from?: DateArg<Date> | null;
};

type CalendarDayListing<T extends CalendarEntry> = {
    title: string & DateArg<Date>;
    data: CalendarList<T>;
    key: string;
};

export const CalendarGroup = <T extends CalendarEntry = CalendarEntry>({
    calendar,
    from,
    asStatic = false,
}: CalendarGroupProps<T>) => {
    const { background } = useTheme();
    const { date } = useContext(CalendarContext);
    const { cycle, cannot } = useProfile();

    const current_date = formatDateString(from ?? date);

    const cycle_day = useMemo(() => {
        if (!cycle.tracked || cannot("view", "cycle") || cycle.periods.length === 0) {
            return 0;
        }

        const last = cycle.periods.findLast((period) => {
            return period.started_at && isSameDayOrEarlier(period.started_at, current_date);
        });

        return last ? differenceInDays(current_date, last.started_at as DateArg<Date>) + 1 : 0;
    }, [cycle, current_date, cannot]);

    const displayed_calendar = useMemo<CalendarDayListing<T>[]>(
        () =>
            Object.entries(calendar)
                .map(([d, j]) => ({
                    title: d,
                    data: j,
                    key: d,
                }))
                .filter(({ data }) => data.length > 0),
        [calendar],
    );

    const renderItem = useCallback(
        ({ item }: { item: T }) => (
            <View style={[styles.listing, background.secondary]} key={item.id}>
                {isJournal(item) ? <JournalListing journal={item} /> : <EventListing event={item} />}
            </View>
        ),
        [background.secondary],
    );

    const formatDay = useCallback(
        (date: DateArg<Date>) =>
            cycle_day > 0
                ? `${formatShortDateForHumans(date)} • Cycle day ${cycle_day}`
                : formatShortDateForHumans(date),
        [cycle_day],
    );

    const emptyComponent = <NoRecords>Nothing recorded for {formatDay(current_date)}</NoRecords>;

    const renderAgenda = useCallback(
        (records: CalendarDayListing<T>[]) => (
            <AgendaList
                sections={records}
                renderItem={renderItem}
                avoidDateUpdates={false}
                scrollToNextEvent={records.length > 0}
                sectionStyle={{ ...styles.section, ...background.secondary }}
                dayFormatter={formatDay}
                ListEmptyComponent={emptyComponent}
            />
        ),
        [emptyComponent, renderItem, formatDay, background.secondary],
    );

    const renderStatic = useCallback(
        (records: CalendarDayListing<T>[]) =>
            records.length > 0
                ? records.map(({ title, data, key }) => (
                      <View key={key} style={Gap.xs}>
                          <Title size="sm" colour="secondary">
                              {formatDay(title)}
                          </Title>
                          {data.map((item) => renderItem({ item }))}
                      </View>
                  ))
                : emptyComponent,
        [emptyComponent, renderItem, formatDay],
    );

    return (asStatic ? renderStatic : renderAgenda)(displayed_calendar);
};

const styles = StyleSheet.create({
    listing: {
        paddingBottom: 12,
    },
    section: {
        marginTop: -1,
    },
});
